/**
 * Machine-readable registry of UI surfaces for Environment Appearance tabs and harmonized theming.
 * Paths are relative to `client/src/` (for docs and existence checks).
 */

export type UiSurfaceType = 'dialog' | 'menu' | 'drawer' | 'panel' | 'toolbar' | 'controller'

export interface UiSurfaceMember {
  id: string
  title: string
  type: UiSurfaceType
  /** Paths under client/src/ */
  primaryFiles: string[]
}

export interface UiSurfaceCategory {
  id: string
  label: string
  description: string
  members: UiSurfaceMember[]
}

/** Categories with full game-in-room migration first; others list surfaces for documentation / future slots. */
export const UI_SURFACE_CATEGORIES: UiSurfaceCategory[] = [
  {
    id: 'gameInRoom',
    label: 'Game (in-room)',
    description:
      'Bottom toolbar, profile menu, guide, room info, performance monitor, map editor shell, knock dialog, and in-game panels. Optional surface colors override the global palette for this cluster only.',
    members: [
      {
        id: 'game-bottom-toolbar',
        title: 'Bottom toolbar',
        type: 'toolbar',
        primaryFiles: [
          'components/GameMenu/BottomToolbar.tsx',
          'components/GameMenu.tsx',
          'components/GameMenu/gameMenuSurfaces.ts',
        ],
      },
      {
        id: 'game-livekit-call-dock',
        title: 'LiveKit call / AV dock',
        type: 'toolbar',
        primaryFiles: [
          'components/GameMenu/GameMenuCallControls.tsx',
          'components/LiveKitVideoDisplay/context/LiveKitCallControlsContext.tsx',
        ],
      },
      {
        id: 'game-profile-menu',
        title: 'Profile menu + dropdown',
        type: 'menu',
        primaryFiles: ['components/GameMenu/ProfileMenu.tsx'],
      },
      {
        id: 'game-guide-dialog',
        title: 'Guide / help dialog',
        type: 'dialog',
        primaryFiles: ['components/GameMenu/GuideDialog.tsx', 'components/GameMenu/useGuideDialog.ts'],
      },
      {
        id: 'game-admin-player-panel',
        title: 'Admin player management panel',
        type: 'panel',
        primaryFiles: ['components/GameMenu/AdminPlayerManagementPanel.tsx'],
      },
      {
        id: 'game-stress-test-panel',
        title: 'Stress test panel',
        type: 'panel',
        primaryFiles: ['components/GameMenu/StressTestPanel.tsx'],
      },
      {
        id: 'game-room-info',
        title: 'Room info',
        type: 'dialog',
        primaryFiles: ['components/RoomInfo.tsx'],
      },
      {
        id: 'game-performance-monitor',
        title: 'Performance monitor',
        type: 'dialog',
        primaryFiles: ['components/PerformanceMonitor.tsx'],
      },
      {
        id: 'game-knock-owner',
        title: 'Virtual room knock (owner)',
        type: 'dialog',
        primaryFiles: ['components/VirtualRoom/KnockOwnerDialog.tsx'],
      },
      {
        id: 'game-map-editor-shell',
        title: 'Map editor (React shell)',
        type: 'dialog',
        primaryFiles: ['components/MapEditor/MapEditor.tsx'],
      },
      {
        id: 'game-menu-controller',
        title: 'Game menu controller (composition root)',
        type: 'controller',
        primaryFiles: ['controllers/GameMenuController.tsx'],
      },
    ],
  },
  {
    id: 'lobbyShell',
    label: 'Lobby & main menu',
    description: 'Main menu shell, lobby dialogs, and room flows. Uses global palette today; listed for inventory.',
    members: [
      {
        id: 'lobby-main-menu',
        title: 'Main menu shell',
        type: 'panel',
        primaryFiles: ['components/MainMenu.tsx', 'components/styles/MainMenu.styles.ts'],
      },
      {
        id: 'lobby-create-room',
        title: 'Create room / permissions / password dialogs',
        type: 'dialog',
        primaryFiles: [
          'components/styles/PermissionsDialog.styles.ts',
          'components/CreateRoomDialog.tsx',
        ],
      },
      {
        id: 'lobby-profile-edit',
        title: 'Profile edit dialog',
        type: 'dialog',
        primaryFiles: ['components/styles/ProfileEditDialog.styles.ts'],
      },
    ],
  },
  {
    id: 'company',
    label: 'Company management',
    description: 'Company suite dialogs and tables.',
    members: [
      {
        id: 'company-styled-shell',
        title: 'Shared dialog shell',
        type: 'dialog',
        primaryFiles: ['components/CompanyManagement/styles/StyledComponents.tsx'],
      },
    ],
  },
  {
    id: 'auth',
    label: 'Auth & onboarding',
    description: 'Authentication and profile gates.',
    members: [
      {
        id: 'auth-dialog',
        title: 'Authentication dialog',
        type: 'dialog',
        primaryFiles: ['components/AuthenticationDialog.tsx', 'components/styles/AuthenticationDialog.styles.ts'],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin & platform',
    description: 'Global admin and environment config surfaces.',
    members: [
      {
        id: 'admin-environment',
        title: 'Environment config (Appearance)',
        type: 'panel',
        primaryFiles: [
          'components/MainMenu/sections/admin/EnvironmentConfig/sections/AppearanceSettings.tsx',
        ],
      },
    ],
  },
]

export function getUiSurfaceCategory(id: string): UiSurfaceCategory | undefined {
  return UI_SURFACE_CATEGORIES.find((c) => c.id === id)
}
